/**
 * Contador propio de clics de partners que cobran POR CLIC (hoy Facile).
 *
 * Cada clic de Facile llega a `/partners/facile?token=<JWE>` con su propio
 * token. Hasta ahora el clic solo se contaba en PostHog (`$pageview`); el CRM
 * lo cuenta ahora él mismo en `POST {CRM}/api/v1/public/landing-hits`, que
 * hashea el token (SHA-256, nunca lo guarda en claro) y es idempotente por
 * `clickId`, así que un doble disparo no cuenta dos veces.
 *
 * Es medición de facturación, no analítica: sin cookies, sin visitor_id y sin
 * gate de consentimiento; solo viaja el token del clic del partner.
 *
 * Transporte: `sendBeacon` con el body como STRING (=> `text/plain`, petición
 * simple: sin preflight CORS). Fallback a `fetch` no-cors + keepalive. Nunca
 * lanza ni afecta a la página: la respuesta (204) no se lee.
 */

export type PartnerLandingHit = {
  partner: string;
  landing: string;
  /** Token crudo del clic (el server lo hashea). */
  clickId: string;
  path: string;
};

const CRM_BASE_URL_PRD = "https://crm.life5.com";
const CRM_BASE_URL_TEST = "https://crm.test.life5.com";
const LANDING_HITS_PATH = "/api/v1/public/landing-hits";

/**
 * Base del CRM según el entorno del build. `REACT_APP_CRM_BASE_URL` (opcional)
 * manda si está; si no, PRD/TEST por `REACT_APP_ENVIROMENT`. En DEV/local sin
 * override no se envía nada (null).
 */
export const crmBaseUrl = (): string | null => {
  const override = (process.env.REACT_APP_CRM_BASE_URL ?? "")
    .trim()
    .replace(/\/+$/, "");
  if (override) return override;
  if (process.env.REACT_APP_ENVIROMENT === "PRD") return CRM_BASE_URL_PRD;
  if (process.env.REACT_APP_ENVIROMENT === "TEST") return CRM_BASE_URL_TEST;
  return null;
};

export const sendPartnerLandingHit = (hit: PartnerLandingHit): void => {
  try {
    if (!hit.clickId) return;
    const base = crmBaseUrl();
    if (!base) return;
    const url = `${base}${LANDING_HITS_PATH}`;
    const body = JSON.stringify(hit);

    if (
      typeof navigator !== "undefined" &&
      typeof navigator.sendBeacon === "function"
    ) {
      try {
        // false = el navegador no lo encoló (cola llena): cae al fetch.
        if (navigator.sendBeacon(url, body)) return;
      } catch {
        /* sendBeacon no disponible en este contexto: cae al fetch */
      }
    }

    if (typeof fetch !== "function") return;
    fetch(url, {
      method: "POST",
      body,
      keepalive: true,
      mode: "no-cors",
      credentials: "omit",
      headers: { "Content-Type": "text/plain;charset=UTF-8" },
    }).catch(() => {});
  } catch {
    /* nunca afecta a la página */
  }
};
